import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { fmtDate, fmtDateTime } from '../../shared/format'
import type { Project } from '../types'
import { PriceLine, ProgressBar, ProjectKindBadge, ProjectStatusBadge, ScheduleBadge } from './ProjectBits'

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[12rem_1fr] sm:gap-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-sm font-medium">{children}</dd>
    </div>
  )
}

export function ProjectInfoPanel({ project: p }: { project: Project }) {
  const { t } = useTranslation()
  return (
    <dl className="divide-y px-4 sm:px-5">
      <Row label={t('projects.fields.status')}>
        <ProjectStatusBadge status={p.status} />
      </Row>
      <Row label={t('projects.fields.deployment')}>
        {p.deploymentType ? t(`projects.deployment.${p.deploymentType}`) : t('projects.deploymentNone')}
        <span className="block text-xs font-normal text-muted-foreground">{p.deploymentType ? t(`projects.deploymentHint.${p.deploymentType}`) : t('projects.deploymentDerived')}</span>
      </Row>
      <Row label={t('projects.stats.licensePrice')}>
        <PriceLine amounts={p.basePrice} />
        <span className="block text-xs font-normal text-muted-foreground">{t('projects.licensePriceHint')}</span>
      </Row>
      <Row label={t('projects.fields.kind')}>
        <ProjectKindBadge kind={p.kind} />
        <span className="block text-xs font-normal text-muted-foreground">{t(`projects.kindHint.${p.kind}`)}</span>
      </Row>
      <Row label={t('projects.fields.period')}>
        <span className="inline-flex flex-wrap items-center gap-2">
          {fmtDate(p.startDate)} – {p.endDate ? fmtDate(p.endDate) : t('projects.fields.noEnd')}
          <ScheduleBadge endDate={p.endDate} status={p.status} />
        </span>
      </Row>
      <Row label={t('projects.stats.progress')}>
        <ProgressBar value={p.progress} label={t('projects.stats.progress')} className="max-w-sm" />
        {p.overdueTasks > 0 ? (
          <span className="mt-1 block text-xs font-medium text-destructive">
            {p.overdueTasks} {t('projects.schedule.overdueTasks')}
          </span>
        ) : null}
      </Row>
      <Row label={t('projects.stats.version')}>{p.version ?? '—'}</Row>
      <Row label={t('projects.fields.summary')}>
        {t('projects.fields.summaryValue', { modules: p.moduleCount, members: p.team.length, tasks: p.activeTasks })}
      </Row>
      <Row label={t('projects.fields.updatedAt')}>{fmtDateTime(p.updatedAt)}</Row>
    </dl>
  )
}
